import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, Store, select } from '@ngrx/store'
import {
  catchError,
  concatAll,
  concatMap,
  defaultIfEmpty,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators'
import { EMPTY, from, lastValueFrom, throwError, timer } from 'rxjs'
import { ProgramService } from '../../../api/program/program.service'
import { AppState } from '../index'
import { mergeMapWithInput, rejectError, rejectErrorWithInput } from '../../../api/util'
import {
  saveTowerPreferences,
  saveTowerPreferenesFailure,
  saveTowerPreferenesSuccess,
  setProgramNameAndDescription,
  setProgramNameAndDescriptionFailure,
  setProgramNameAndDescriptionSuccess,
  updateProgramIndex,
  updateProgramIndexFailure,
  updateProgramIndexSuccess,
  updateTailMetrics,
  updateTailMetricsFailure,
  updateTailMetricsSuccess,
  importBulkLossSets,
  importBulkLossSetsSuccess,
  handleSwapBulkLossSets,
  swapBulkLossSetsSuccess,
  updateProgramIndexes,
  updateProgramIndexesFailure,
  updateProgramIndexesSuccess,
  updateFolderID,
  updateFolderIDFailure,
  updateFolderIDSuccess,
  updateCounts,
  updateCountsFailure,
  updateCountsSuccess,
  swapBulkLossSetsProcessingComplete,
  swapBulkLossSetsIntermediateSuccess,
} from './program.actions'
import { Observable, of } from 'rxjs'
import { selectCurrentStudyPrograms } from './program.selectors'
import { ReturnPeriodRow } from '../../../analysis/model/metrics.model'
import { selectCurrentStructureID } from '../broker/broker.selectors'
import { MatDialog } from '@angular/material/dialog'
import { Program } from '../../model/program.model'
import { MessageDialogService } from '@shared/message-dialog.service'

@Injectable()
export class ProgramEffects {
  constructor(
    private actions$: Actions,
    private programService: ProgramService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private messageService: MessageDialogService,
  ) {}

  setTowerPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveTowerPreferences),
      switchMap((action) => {
        return this.programService.setTowerPreferences(action.id, action.preferences)
      }),
      switchMap((res) => {
        const actions = []
        if (res.error) {
          actions.push(saveTowerPreferenesFailure({ error: res.error }))
        } else if (res.data !== undefined) {
          actions.push(saveTowerPreferenesSuccess(res.data))
        }
        return actions
      }),
    )
  })

  setProgramNameAndDescription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setProgramNameAndDescription),
      mergeMap(({ structure, name, description }) =>
        this.programService.update(structure.id, {
          ...structure,
          label: name,
          description,
        }),
      ),
      rejectError((error) => this.store.dispatch(setProgramNameAndDescriptionFailure({ error }))),
      filter(({ label }) => label != null && label.length > 0),
      map(({ id, label, description }) =>
        setProgramNameAndDescriptionSuccess({ id, name: label, description }),
      ),
    )
  })

  swapBulkLossSetsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(swapBulkLossSetsSuccess, importBulkLossSetsSuccess),
        map(() => {
          location.reload()
        }),
      ),
    { dispatch: false },
  )

  updateProgramIndex$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateProgramIndex),
      mergeMap(({ structure, index }) =>
        this.programService.update(structure.id, {
          ...structure,
          position_index: index,
        }),
      ),
      rejectError((error) => this.store.dispatch(updateProgramIndexFailure({ error }))),
      map(({ id, position_index }) =>
        // tslint:disable-next-line: no-non-null-assertion
        updateProgramIndexSuccess({ id, index: position_index! }),
      ),
    )
  })

  updateProgramIndexes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateProgramIndexes),
      mergeMap(({ req }) => {
        return this.programService.updatePositionIndexes(req)
      }),
      rejectError((error) => this.store.dispatch(updateProgramIndexesFailure({ error }))),
      map((study) => {
        return updateProgramIndexesSuccess({ structures: study })
      }),
    )
  })

  updateProgramFolderID$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateFolderID),
      withLatestFrom(this.store.pipe(select(selectCurrentStudyPrograms))),
      mergeMap(([action, structures]) => {
        const { structureId, folderID } = action
        const structure = structures.filter((s) => {
          return s.id === structureId
        })[0]
        return this.programService.update(structureId, {
          ...structure,
          folderID,
        })
      }),
      rejectError((error) => {
        return this.store.dispatch(updateFolderIDFailure({ error }))
      }),
      map(({ id, folderID }) => {
        return updateFolderIDSuccess({
          structureId: id,
          folderID,
        })
      }),
    )
  })

  updateProgramCounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateCounts),
      withLatestFrom(
        this.store.pipe(select(selectCurrentStructureID)),
        this.store.pipe(select(selectCurrentStudyPrograms)),
      ),
      mergeMap(([action, structureId, structures]) => {
        const { fotCount, quoteCount } = action
        const structure = structures.filter((s) => {
          return s.id === structureId
        })[0]

        if (structureId) {
          return this.programService.update(structureId, {
            ...structure,
            fotCount,
            quoteCount,
          })
        } else {
          return of([])
        }
      }),
      rejectError((error) => {
        return this.store.dispatch(updateCountsFailure({ error }))
      }),
      map(({ id, fotCount, quoteCount }) => {
        return updateCountsSuccess({
          structureId: id,
          fotCount: fotCount ?? 0,
          quoteCount: quoteCount ?? 0,
        })
      }),
    )
  })

  updateTailMetrics$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateTailMetrics),
      mergeMapWithInput(({ id, tailMetrics }) => {
        const tailMetricsOptions = {
          ...tailMetrics,
          returnPeriodData: [] as ReturnPeriodRow[],
        }
        return this.programService.putTailMetricsOptions(id, tailMetricsOptions)
      }),
      rejectErrorWithInput((error) => this.store.dispatch(updateTailMetricsFailure({ error }))),
      map(([_, { id, tailMetrics }]) => {
        return updateTailMetricsSuccess({ id, tailMetrics })
      }),
    )
  })

  handleAddLossSets(selectedStructures: Program[], result: any): Observable<Action> {
    let time = 0

    return from(selectedStructures).pipe(
      concatMap((structure, i) => {
        const updatedStructure =
          structure.description === 'No Modeling' ? { ...structure, description: '' } : structure

        const payload = {
          structure: updatedStructure,
          parentGrossPortfolioID: result.parentGrossPortfolioID || '',
          analysisProfileID: result.analysisProfileID || '',
          isLast: i === selectedStructures.length - 1,
        }

        const actions: Action[] = [
          setProgramNameAndDescription({
            structure: updatedStructure,
            name: updatedStructure.label,
            description: updatedStructure.description,
          }),
          importBulkLossSets(payload),
        ]

        time += 1200

        return timer(time).pipe(map(() => actions))
      }),
      concatAll(),
    )
  }
}
